/**
 * Display helpers of the Cash Transfers container (C23). Display-only over decimal strings (BigInt, no `number` for money,
 * ADR-02 §2.9): the old two-decimal amount format, the flags and symbols of the old `availableCurrencies`
 * (CashTransfers.tsx:69-119), the deficit shown by the warnings. Every posted figure comes from the server.
 */
import { formatWithCommas } from "@faylasuf/ui";
import type { Translate } from "@faylasuf/i18n";
import type { TransferCurrencyData, TransferRowData } from "../../api/types.js";
import { toScaled } from "../customers/format.js";

/** Old flag table (CashTransfers.tsx:92-105) + IQD / USD (:71-72); anything else 🪙. */
const FLAGS: Readonly<Record<string, string>> = {
  IQD: "🇮🇶", USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", TRY: "🇹🇷", SAR: "🇸🇦", AED: "🇦🇪", KWD: "🇰🇼", JOD: "🇯🇴",
  EGP: "🇪🇬", CAD: "🇨🇦", AUD: "🇦🇺", CHF: "🇨🇭", JPY: "🇯🇵", CNY: "🇨🇳",
};
export const flagOf = (code: string): string => FLAGS[code] ?? "🪙";

/** Old `formatRemittanceNumber` (:121-138): thousands separators, exactly two decimals (half up), "-" kept. */
export function money2(s: string | null | undefined): string {
  const v = toScaled(s ?? "0"); // scale 6
  const neg = v < 0n;
  const a = neg ? -v : v;
  const cents = (a + 5000n) / 10000n; // 10^6 → 10^2, half up
  const int = (cents / 100n).toString();
  const frac = (cents % 100n).toString().padStart(2, "0");
  return `${neg && cents !== 0n ? "-" : ""}${formatWithCommas(int)}.${frac}`;
}

export function symbolOf(code: string, currencies: readonly TransferCurrencyData[]): string {
  if (code === "IQD") return "د.ع";
  if (code === "USD") return "$";
  return currencies.find((c) => c.code === code)?.symbol ?? code;
}

export function currencyName(code: string, currencies: readonly TransferCurrencyData[], t: Translate, locale: string): string {
  if (code === "IQD") return t("ct.cur_iqd");
  if (code === "USD") return t("ct.cur_usd");
  const c = currencies.find((x) => x.code === code);
  return c ? (locale === "ar" ? c.nameAr : c.nameEn) : code;
}

/** "3,000,000.00 د.ع". */
export const amountWith = (s: string, code: string, currencies: readonly TransferCurrencyData[]): string => `${money2(s)} ${symbolOf(code, currencies)}`;

/** The row's payment type of the old list (cash / credit / trust). */
export const paymentOf = (r: TransferRowData): "cash" | "credit" | "trust" => r.paymentKind;

/** Display comparison of two decimal strings. */
export const gt = (a: string, b: string): boolean => toScaled(a) > toScaled(b);
export const lte0 = (a: string): boolean => toScaled(a) <= 0n;
